import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { gallery } from '../data/mock';

export const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState('all');
  const [currentIndex, setCurrentIndex] = useState(0);

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'security', label: 'Security' },
    { id: 'facility', label: 'Facility Management' },
    { id: 'housekeeping', label: 'Housekeeping' },
    { id: 'team', label: 'Our Team' }
  ];

  const filteredGallery = selectedCategory === 'all'
    ? gallery
    : gallery.filter(item => item.category === selectedCategory);

  const openModal = (category, startIndex) => {
    setCurrentCategory(category);
    setCurrentIndex(startIndex);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const modalImages = gallery.filter(item => item.category === currentCategory);

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((currentIndex + 1) % modalImages.length);
  };

  const previousImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((currentIndex - 1 + modalImages.length) % modalImages.length);
  };

  return (
    <section id="gallery" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
            Our Gallery
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
            See Our Team in Action
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Professional, trained, and dedicated workforce delivering excellence. Click any image to view more.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredGallery.map((item) => {
            const categoryImages = gallery.filter(img => img.category === item.category);
            const categoryIndex = categoryImages.findIndex(img => img.id === item.id);
            
            return (
              <div
                key={item.id}
                onClick={() => openModal(item.category, categoryIndex)}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 aspect-[4/3] cursor-pointer"
              >
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
                
                {/* Title */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-xl font-bold text-white">
                    {item.title}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredGallery.length === 0 && (
          <div className="text-center py-16">
            <p className="text-slate-500 text-lg">No images found for this category</p>
          </div>
        )}
      </div>

      {/* Modal Slideshow */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div className="relative max-w-6xl max-h-[90vh] w-full px-4">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 flex items-center justify-center transition-all shadow-xl"
            >
              <X className="w-7 h-7 text-white" />
            </button>

            {/* Previous Button */}
            {modalImages.length > 1 && (
              <button
                onClick={previousImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 flex items-center justify-center transition-all shadow-xl"
              >
                <ChevronLeft className="w-7 h-7 text-white" />
              </button>
            )}

            {/* Next Button */}
            {modalImages.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 flex items-center justify-center transition-all shadow-xl"
              >
                <ChevronRight className="w-7 h-7 text-white" />
              </button>
            )}

            {/* Image */}
            <div 
              className="flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={modalImages[currentIndex]?.url}
                alt={modalImages[currentIndex]?.title}
                className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl"
              />
            </div>

            {/* Image Counter */}
            {modalImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full shadow-xl">
                <p className="text-white text-base font-medium">
                  {currentIndex + 1} / {modalImages.length}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};
